import { useContext, useEffect, useState } from "react";
import {
    Box, Paper, Divider, Collapse,
} from "@mui/material";
import UserContext from "../User/UserSettings/UserContext";
import { useInspectionStore } from "../Stores/InspectionStore";
import { useBayStore } from "../Stores/BayStore";
import { useShallow } from "zustand/react/shallow";
import FormHeader from "./Components/FormHeader";
import GeneralInformationSection from "./GeneralInformationSection";
import BayInspectionsList from "./BayInspectionsList";
import FloatingSaveButton from "./Components/FloatingSaveButton";
import SheetSelector from "./Components/SheetSelector";
import { SearchFilters } from "../Types/InspectionTypes";
import API from "../api2";
import useNotification from "../Hooks/useNotification";

const InspectionForm = () => {
    const { user } = useContext(UserContext) || { user: null };
    const notify = useNotification();
    const { updateField, setAvailableBays, resetForm, loadInspectionData } = useInspectionStore(
        useShallow((state) => ({
            updateField: state.updateField,
            setAvailableBays: state.setAvailableBays,
            resetForm: state.resetForm,
            loadInspectionData: state.loadInspectionData,
        }))
    );
    const plenumSetPoint = useInspectionStore((state) => state.formData.plenumSetPoint);
    const { initializeBays, recalculateAllBaysAvgSpDeltaT, resetBays, setBays } = useBayStore(
        useShallow((state) => ({
            initializeBays: state.initializeBays,
            recalculateAllBaysAvgSpDeltaT: state.recalculateAllBaysAvgSpDeltaT,
            resetBays: state.resetBays,
            setBays: state.setBays,
        }))
    );

    const [saveSuccess, setSaveSuccess] = useState(false);
    const [showForm, setShowForm] = useState(false);
    const [currentInspectionId, setCurrentInspectionId] = useState<number | null>(null);
    console.log(user)
    const isMobile = user?.mobile || false;

    // Automatically show new form on initial load
    useEffect(() => {
        handleCreateNew();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []); // Only run once on mount

    // Fetch bays for the selected location
    const fetchBaysForLocation = async (locationId: string) => {
        const bayResult = await API.getBaysByLocation({ locationId });
        if (bayResult.error) {
            notify(bayResult.message, 'error');
            return;
        }

        if (!bayResult.data || bayResult.data.length === 0) {
            notify('No bays found for the selected storage. Please contact an admin to have a bay added.', 'warning');
            setAvailableBays([]);
            initializeBays([]);
            return;
        }

        const bayNames = bayResult.data.map((bay: any) => bay.bayName);
        setAvailableBays(bayNames);
        // Initialize bays with the full objects (includes id and bayName)
        initializeBays(bayResult.data);
    };

    const handleStorageChange = (storageId: string) => {
        updateField('storage', storageId);
        if (storageId) {
            fetchBaysForLocation(storageId);
        }
    };

    const handleCreateNew = () => {
        resetForm();
        resetBays();
        setCurrentInspectionId(null);

        // Set default customer to first in list
        if (user?.customers && user.customers.length > 0) {
            updateField('customer', user.customers[0].customer);
        }

        setShowForm(true);
    };

    const handleTabChange = (tabIndex: number) => {
        // When switching to "Load Sheet" tab (index 1), reset and minimize the form
        if (tabIndex === 1) {
            resetForm();
            resetBays();
            setCurrentInspectionId(null);
            setShowForm(false);
        } else if (tabIndex === 0) {
            // When switching to "New Sheet" tab, automatically create a new form
            handleCreateNew();
        }
    };

    const handleSearch = async (filters: SearchFilters) => {
        const response = await API.getInspections({
            customer: filters.customer || undefined,
            locationId: filters.locationId || undefined,
            dateFrom: filters.dateFrom || undefined,
            dateTo: filters.dateTo || undefined,
        });

        if (response.error) {
            notify(response.message, 'error');
            return [];
        }

        return response.data || [];
    };

    const handleLoadSheet = async (inspectionId: number) => {
        // Reset form and bays FIRST to clear any previous data
        resetForm();
        resetBays();
        setCurrentInspectionId(null);

        const response = await API.getInspectionById({ id: inspectionId });

        if (response.error) {
            notify(response.message, 'error');
            return;
        }

        const inspection = response.data;
        console.log(inspection)
        // Load inspection data into form state`
        loadInspectionData(inspection);

        // Fetch available bays for the location FIRST (for the dropdown/available bays list)
        // This sets the availableBays but doesn't overwrite bay data
        if (inspection.location) {
            const bayResult = await API.getBaysByLocation({ locationId: inspection.location.toString() });
            if (!bayResult.error && bayResult.data) {
                const bayNames = bayResult.data.map((bay: any) => bay.bayName);
                setAvailableBays(bayNames);
            }
        }

        // THEN load the actual bay data from the inspection (this will properly structure sprouting/breakdown)
        if (inspection.bays && inspection.bays.length > 0) {
            setBays(inspection.bays);
        } else {
            // If no bays in the inspection, initialize empty bays for the location
            resetBays();
        }

        setCurrentInspectionId(inspectionId);
        setShowForm(true);
        notify('Inspection loaded successfully', 'success');
    };

    const handleSave = async () => {
        // Validate required fields
        const { formData } = useInspectionStore.getState();
        const { bays } = useBayStore.getState();

        if (!formData.customer || formData.customer === '') {
            notify('Please select an account before saving', 'error');
            return;
        }

        if (!formData.storage || formData.storage === '') {
            notify('Please select a storage location before saving', 'error');
            return;
        }

        if (!formData.dateServiced || formData.dateServiced === '') {
            notify('Please select a date serviced before saving', 'error');
            return;
        }

        // Combine form data with bay data before saving
        const inspectionData = {
            creator: user.id,
            ...formData,
            bays,
        };

        console.log('Saving inspection data:', inspectionData);
        console.log('Bay structure:', bays[0]); // Log first bay to check structure

        let response;
        if (currentInspectionId) {
            // Update existing inspection
            response = await API.updateInspection({
                id: currentInspectionId,
                ...inspectionData,
            });
        } else {
            // Create new inspection
            response = await API.addInspection(inspectionData);
        }

        if (response.error) {
            notify(response.message, 'error');
            return;
        }

        notify(
            currentInspectionId
                ? 'Inspection updated successfully'
                : 'Inspection created successfully',
            'success'
        );
        setSaveSuccess(true);
        setTimeout(() => setSaveSuccess(false), 3000);

        // Update current inspection ID if this was a new creation
        if (!currentInspectionId && response.data?.id) {
            setCurrentInspectionId(response.data.id);
        }
    };

    const handleDelete = async () => {
        if (!currentInspectionId) {
            notify('No inspection to delete', 'error');
            return;
        }

        const response = await API.deleteInspection({ id: currentInspectionId });

        if (response.error) {
            notify(response.message, 'error');
            return;
        }

        notify('Inspection deleted successfully', 'success');
        
        // Reset the form and hide it
        resetForm();
        resetBays();
        setCurrentInspectionId(null);
        setShowForm(false);
    };

    const handleDeleteFromSearch = async (inspectionId: number) => {
        const response = await API.deleteInspection({ id: inspectionId });

        if (response.error) {
            notify(response.message, 'error');
            throw new Error(response.message);
        }

        notify('Inspection deleted successfully', 'success');
        
        // If the deleted inspection is currently loaded, reset the form
        if (currentInspectionId === inspectionId) {
            resetForm();
            resetBays();
            setCurrentInspectionId(null);
            setShowForm(false);
        }
    };

    // When plenum set point changes, recalculate all bays
    useEffect(() => {
        if (plenumSetPoint !== '') {
            recalculateAllBaysAvgSpDeltaT(plenumSetPoint);
        }
    }, [plenumSetPoint, recalculateAllBaysAvgSpDeltaT]);

    const renderContent = () => (
        <>
            <FormHeader
                isMobile={isMobile}
                showSuccess={saveSuccess}
                inspectionId={currentInspectionId}
                onDelete={handleDelete}
            />

            <GeneralInformationSection
                isMobile={isMobile}
                customers={user?.customers || []}
                locations={user?.locations || []}
                onStorageChange={handleStorageChange}
            />

            <Divider sx={{ my: isMobile ? 2 : 4 }} />

            <BayInspectionsList
                isMobile={isMobile}
            />
        </>
    );

    return (
        <Box sx={{ p: isMobile ? 1 : 3, maxWidth: 1400, mx: 'auto', pb: isMobile ? 10 : 3 }}>
            {/* Sheet Selector */}
            <SheetSelector
                customers={user?.customers || []}
                locations={user?.locations || []}
                onLoadSheet={handleLoadSheet}
                onSearch={handleSearch}
                onTabChange={handleTabChange}
                isMobile={isMobile}
                isAdmin={user?.accountLevel === 'Admin'}
                onDeleteSheet={handleDeleteFromSearch}
            />

            {/* Inspection Form - Collapsible */}
            <Collapse in={showForm} timeout={500}>
                {isMobile ? (
                    // Mobile view without Paper wrapper
                    <Box sx={{ mb: 2 }}>
                        {renderContent()}
                    </Box>
                ) : (
                    // Desktop view with Paper wrapper
                    <Paper elevation={2} sx={{ p: 3, mb: 3 }}>
                        {renderContent()}
                    </Paper>
                )}

                {/* Floating Save Button */}
                <FloatingSaveButton
                    onSave={handleSave}
                    isMobile={isMobile}
                    showSuccess={saveSuccess}
                />
            </Collapse>
        </Box>
    );
};

export default InspectionForm;
